import { useState } from "react";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Group, Stack, Text, Textarea } from "@/components/ui";
import { IconSend } from "@tabler/icons-react";
import dayjs from "dayjs";
import {
  useCrearSolicitudCatalogo,
  useEstadoSolicitudCatalogo,
} from "../hooks/use-solicitud-catalogo";

/** Tipos de catálogo sobre los que un solicitante puede pedir un alta (P38). */
export type TipoCatalogoSolicitud = "Proveedor" | "CuentaContablePorClave";

/** Copy específico por tipo · el resto del flujo es idéntico. */
const COPY: Record<
  TipoCatalogoSolicitud,
  { pendienteCola: string; tituloModal: string; cuerpoModal: (clave: string) => string; boton: string }
> = {
  Proveedor: {
    pendienteCola: "podrás asignar el proveedor",
    tituloModal: "Solicitar alta de proveedor",
    cuerpoModal: (clave) => `dar de alta el proveedor con RFC ${clave}`,
    boton: "Solicitar alta al administrador",
  },
  CuentaContablePorClave: {
    pendienteCola: "la clave tendrá su cuenta contable",
    tituloModal: "Solicitar cuenta contable",
    cuerpoModal: (clave) => `configurar la cuenta contable de la clave producto/servicio ${clave}`,
    boton: "Solicitar cuenta al administrador",
  },
};

/**
 * Botón + estado de la solicitud de alta de un elemento de catálogo (P38).
 * Se monta dentro del aviso de "no existe en el catálogo":
 *  · Proveedor: el RFC emisor sin proveedor (paso Proveedor).
 *  · CuentaContablePorClave: la clave producto/servicio sin cuenta contable
 *    mapeada (línea del prorrateo · equivalente del dbo.Tag legado).
 * Estados: sin solicitud → botón + modal con comentario opcional · Pendiente →
 * aviso · Rechazada → motivo + re-solicitar · Atendida → aviso + re-solicitar.
 */
export function SolicitarAltaCatalogo({
  tipo,
  clave,
  descripcion,
  solicitudPagoId,
  numeroSolicitudPago,
}: {
  tipo: TipoCatalogoSolicitud;
  clave: string;
  descripcion?: string;
  solicitudPagoId?: string;
  numeroSolicitudPago?: number;
}) {
  const copy = COPY[tipo];
  const { data: existente, isPending } = useEstadoSolicitudCatalogo(tipo, clave);
  const crear = useCrearSolicitudCatalogo();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [comentario, setComentario] = useState("");

  if (isPending) return null;

  const enviar = () =>
    crear.mutate(
      {
        tipo,
        clave,
        descripcion,
        comentario: comentario.trim() || undefined,
        solicitudPagoId,
        numeroSolicitudPago,
      },
      { onSuccess: () => setModalAbierto(false) }
    );

  if (existente?.estado === "Pendiente") {
    return (
      <Text size="sm" fw={500}>
        Alta ya solicitada al administrador el{" "}
        {dayjs(existente.creadoAt).format("DD/MM/YYYY HH:mm")} · en cuanto la atienda {copy.pendienteCola}.
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {existente?.estado === "Rechazada" && (
        <Text size="sm">
          <Text span fw={600}>
            El administrador rechazó una solicitud anterior:
          </Text>{" "}
          {existente.respuestaComentario ?? "sin motivo registrado"}. Puedes volver a solicitarla.
        </Text>
      )}
      {existente?.estado === "Atendida" && (
        <Text size="sm">
          El administrador marcó como atendida una solicitud anterior. Si el elemento sigue sin
          aparecer, solicítalo de nuevo.
        </Text>
      )}
      <Group>
        <Button size="xs" leftSection={<IconSend size={14} stroke={1.7} />} onClick={() => setModalAbierto(true)}>
          {copy.boton}
        </Button>
      </Group>

      <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{copy.tituloModal}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm">
              Se pedirá al administrador {copy.cuerpoModal(clave)}
              {descripcion ? ` (${descripcion})` : ""}. La verá en su bandeja de Solicitudes de
              catálogos.
            </Text>
            <Textarea
              label="Comentario para el administrador"
              placeholder="Opcional · contexto de por qué lo necesitas"
              value={comentario}
              onChange={(e) => setComentario(e.currentTarget.value)}
              maxLength={500}
              rows={2}
            />
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={() => setModalAbierto(false)}>
              Cancelar
            </Button>
            <Button loading={crear.isPending} onClick={enviar}>
              Enviar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
